/**
 * The app time zone, client side.
 *
 * The server buckets every date by one admin-set zone (backend/timeutil.py).
 * If the browser then renders in *its* zone, the two disagree for anyone
 * travelling or working elsewhere: the dashboard heading says one day, the
 * folder the signal landed in says another. So dates and times are formatted in
 * the app zone, and labelled with it, rather than in whatever zone the laptop
 * happens to be set to.
 *
 * Cached for the page's lifetime, in the same shape as lib/llm.ts: the zone
 * changes about as often as the analysis model does, and every route change
 * would otherwise re-ask.
 *
 * Every formatter here degrades rather than throws. Before the fetch resolves —
 * or if it fails — `appTz()` is undefined and Intl falls back to the browser's
 * own zone, which is what the app did before this module existed.
 */

import { api } from "./api";

export interface AppTimezone {
  timezone: string;
  offsetMinutes: number;
  now: string;
}

let _cached: AppTimezone | null = null;
let _inFlight: Promise<AppTimezone> | null = null;

export function invalidateAppTimezone(): void {
  _cached = null;
  _inFlight = null;
}

/** Fetch and cache the app zone. Safe to call on every page render. */
export async function ensureAppTimezone(force = false): Promise<AppTimezone | null> {
  if (force) invalidateAppTimezone();
  if (_cached) return _cached;
  if (!_inFlight) {
    _inFlight = api<AppTimezone>("/app/timezone")
      .then((tz) => {
        _cached = tz;
        return tz;
      })
      .finally(() => {
        _inFlight = null;
      });
  }
  try {
    return await _inFlight;
  } catch {
    // A page that cannot read the zone still has to render. Browser-local
    // formatting is the old behaviour, and better than an empty screen.
    return null;
  }
}

/** The app zone's IANA name, or undefined until it has been fetched. */
export function appTz(): string | undefined {
  return _cached?.timezone;
}

/** The zone label to put next to a time, e.g. "Asia/Singapore". */
export function tzLabel(): string {
  return _cached?.timezone ?? "local time";
}

/** Today in the app zone, as "YYYY-MM-DD".
 *
 * en-CA formats as ISO, which is why it is here rather than a locale that
 * would need reassembling. `toISOString().slice(0, 10)` — what this replaced —
 * is UTC, so the dashboard heading rolled over to tomorrow at 08:00 in
 * Singapore and back to yesterday for anyone west of Greenwich.
 */
export function todayInAppTz(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: appTz(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** A stored instant as a date + time in the app zone, e.g. "19 Aug 2026, 09:04". */
export function fmtDateTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return iso ?? "";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: appTz(),
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

/** As fmtDateTime, plus the weekday and the zone — for a scheduled instant,
 *  where "which day" and "whose clock" are the whole question. */
export function fmtSchedule(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return iso ?? "";
  const stamp = new Intl.DateTimeFormat(undefined, {
    timeZone: appTz(),
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
  return `${stamp} (${tzLabel()})`;
}

/** A stored instant as a date in the app zone, e.g. "19 Aug 2026". */
export function fmtDate(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return iso ?? "";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: appTz(),
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}
